/**
 * Read-only audit-trail view for a validation rule (CZNA-245, optional scope).
 *
 * Renders the `validation_rule_history` snapshots returned by
 * `GET /validation/rules/{id}/history` as a compact MultiColumnList. Lazy:
 * only fetches when `enabled` (the parent opens the History accordion).
 */
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiColumnList, Loading, MessageBanner } from '@folio/stripes/components';
import { useValidationRuleHistory } from '../../hooks/useValidationRules';
import useUserName from '../../hooks/useUserName';
var ChangedByCell = function (_a) {
    var _b;
    var entry = _a.entry;
    var name = useUserName((_b = entry.changedBy) !== null && _b !== void 0 ? _b : null).name;
    return React.createElement(React.Fragment, null, name || entry.changedBy || '—');
};
var RuleHistoryView = function (_a) {
    var _b;
    var ruleId = _a.ruleId, enabled = _a.enabled;
    var intl = useIntl();
    var _c = useValidationRuleHistory(ruleId, enabled), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    var columnMapping = useMemo(function () { return ({
        changedAt: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.column.changedAt" }),
        changedBy: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.column.changedBy" }),
        changeSource: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.column.changeSource" }),
        enabledState: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.column.enabled" }),
    }); }, []);
    var formatter = useMemo(function () { return ({
        changedAt: function (e) { return "".concat(intl.formatDate(e.changedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }), " ").concat(intl.formatTime(e.changedAt, { hour: '2-digit', minute: '2-digit' })); },
        changedBy: function (e) { return React.createElement(ChangedByCell, { entry: e }); },
        changeSource: function (e) { return e.changeSource; },
        enabledState: function (e) {
            var _a;
            return (((_a = e.snapshot) === null || _a === void 0 ? void 0 : _a.enabled)
                ? intl.formatMessage({ id: 'ui-czna-authorities.validationRules.enabled.yes' })
                : intl.formatMessage({ id: 'ui-czna-authorities.validationRules.enabled.no' }));
        },
    }); }, [intl]);
    if (!enabled)
        return null;
    if (isLoading)
        return React.createElement(Loading, null);
    if (isError) {
        return (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.loadError" })));
    }
    var entries = (_b = data === null || data === void 0 ? void 0 : data.history) !== null && _b !== void 0 ? _b : [];
    if (entries.length === 0) {
        return (React.createElement("p", { style: { color: '#666' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.history.empty" })));
    }
    return (React.createElement(MultiColumnList, { contentData: entries, columnMapping: columnMapping, 
        // @ts-expect-error - MCL keys are typed against the entity; 'enabledState' is a derived column
        visibleColumns: ['changedAt', 'changedBy', 'changeSource', 'enabledState'], formatter: formatter, interactive: false }));
};
export default RuleHistoryView;
